/**
 * CadenceCard - one of the four "to review, by check cadence" tiles on the DQ
 * Workbench Overview. A coloured left-border accent encodes urgency, a big
 * total count headlines, and a thin two-segment strip shows how much of the
 * period the team has actually looked at (reviewed vs not-yet-looked).
 *
 * The whole card is a react-router Link to the FP Grid (nav rule: never
 * onClick); the bucket is informational only and the ?market= filter is
 * preserved on the target.
 */
import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { Link } from 'react-router-dom'

import type { CadenceBucket } from '../../../../services/admin/dataExceptionsService'
import {
  dqCadenceAccent,
  dqReviewedSegment,
  dqNotLookedSegment,
} from '../../../../theme/dqStatusColors'

interface Props {
  bucket: CadenceBucket
  label: string
  to: string
}

const CadenceCard: React.FC<Props> = ({ bucket, label, to }) => {
  const { total, reviewed, not_looked } = bucket
  const reviewedPct = total > 0 ? (reviewed / total) * 100 : 0

  return (
    <Card
      component={Link}
      to={to}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'block',
        textDecoration: 'none',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `3px solid ${dqCadenceAccent[bucket.key]}`,
        borderRadius: 2,
        transition: 'box-shadow 0.15s ease',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em' }}
        >
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5, mb: 1 }}>
          {total.toLocaleString()}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            height: 6,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: dqNotLookedSegment,
            mb: 0.75,
          }}
        >
          <Box sx={{ width: `${reviewedPct}%`, bgcolor: dqReviewedSegment }} />
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {reviewed.toLocaleString()} reviewed / {not_looked.toLocaleString()} not yet looked at
        </Typography>
      </CardContent>
    </Card>
  )
}

export default CadenceCard
